import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Building2, Users, BarChart3, FileText, Check, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import { Footer } from "../components/Footer";
import api, { formatApiError } from "../lib/api";
import { pick } from "../lib/pick";
import useSeo from "../hooks/useSeo";

const TIERS = [
  { min: 5, max: 9, pct: 20 },
  { min: 10, max: 19, pct: 30 },
  { min: 20, max: 999, pct: 40 },
];

const discountFor = (seats) => (TIERS.find((x) => seats >= x.min && seats <= x.max) || { pct: 0 }).pct;

export default function B2B() {
  const { t, lang } = useLang();
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState("");
  const [seats, setSeats] = useState(10);
  const [form, setForm] = useState({ name: "", email: "", phone: "", organization: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useSeo({
    lang,
    title: lang === "en" ? "Team & Business Training Plans" : "Εκπαίδευση για Ομάδες & Επιχειρήσεις",
    description:
      lang === "en"
        ? "Train your whole team: bulk seats with volume discounts, a team dashboard and progress tracking for gyms, clubs and organizations."
        : "Εκπαίδευσε όλη σου την ομάδα: πακέτα θέσεων με έκπτωση όγκου, team dashboard και παρακολούθηση προόδου για γυμναστήρια, συλλόγους και οργανισμούς.",
  });

  useEffect(() => {
    api.get("/academy/courses").then((r) => {
      const paid = r.data.filter((c) => c.price > 0);
      setCourses(paid);
      setCourseId(paid[0]?.id || "");
    }).catch(() => {});
  }, []);

  const course = useMemo(() => courses.find((c) => c.id === courseId), [courses, courseId]);
  const pct = discountFor(seats);
  const unit = course ? Math.round(course.price * (1 - pct / 100)) : 0;
  const total = unit * seats;

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", {
        name: form.name, email: form.email, phone: form.phone,
        program: `B2B · ${form.organization} · ${seats} ${t("b2b.seats")} · ${pick(course, "title", lang) || ""}`,
        message: `${form.message}\n\n[${t("b2b.quote")}: ${seats} × ${unit}€ = ${total}€ (−${pct}%)]`,
      });
      setSent(true);
      toast.success(t("b2b.sent"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const benefits = [
    [Users, t("b2b.ben1")],
    [BarChart3, t("b2b.ben2")],
    [FileText, t("b2b.ben3")],
    [Building2, t("b2b.ben4")],
  ];

  return (
    <div data-testid="b2b-page">
      <section className="max-w-7xl mx-auto px-5 sm:px-6 pt-14 pb-16">
        <span className="eyebrow"><span className="w-7 h-px bg-[#1D4ED8]" />{t("b2b.eyebrow")}</span>
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 mt-5 items-start">
          <div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-[#0B1B3A] leading-[1.03]" data-testid="b2b-title">{t("b2b.title")}</h1>
            <p className="text-base text-[#475569] mt-6 max-w-xl">{t("b2b.subtitle")}</p>
            <div className="grid sm:grid-cols-2 gap-4 mt-10">
              {benefits.map(([Icon, text], i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }} className="card-dark p-6 flex gap-4" data-testid={`b2b-benefit-${i}`}>
                  <span className="w-11 h-11 rounded-2xl bg-[#EEF4FF] text-[#1D4ED8] flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></span>
                  <p className="text-sm font-semibold text-[#0B1B3A]">{text}</p>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="card-cream p-7 md:p-8" data-testid="b2b-calculator">
            <h2 className="font-heading font-black text-lg md:text-xl text-[#0B1B3A]">{t("b2b.calcTitle")}</h2>
            <div className="grid grid-cols-3 gap-3 mt-5">
              {TIERS.map((x) => (
                <div key={x.min} className={`rounded-2xl border p-4 text-center ${pct === x.pct ? "bg-[#1D4ED8] text-white border-[#1D4ED8]" : "bg-white border-[#DBEAFE] text-[#0B1B3A]"}`} data-testid={`b2b-tier-${x.min}`}>
                  <p className="font-heading font-black text-2xl">−{x.pct}%</p>
                  <p className="text-[11px] font-bold uppercase tracking-[0.14em] mt-1 opacity-80">{x.max > 100 ? `${x.min}+` : `${x.min}–${x.max}`} {t("b2b.seats")}</p>
                </div>
              ))}
            </div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-[#475569] mt-6">{t("b2b.course")}</label>
            <select data-testid="b2b-course-select" value={courseId} onChange={(e) => setCourseId(e.target.value)} className="input-cream mt-2 w-full">
              {courses.map((c) => <option key={c.id} value={c.id}>{pick(c, "title", lang)} — {c.price}€</option>)}
            </select>
            <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-[#475569] mt-5">{t("b2b.seatsLabel")}: <span className="text-[#1D4ED8]">{seats}</span></label>
            <input data-testid="b2b-seats-range" type="range" min={5} max={60} value={seats} onChange={(e) => setSeats(Number(e.target.value))} className="w-full mt-3 accent-[#1D4ED8]" />
            <div className="mt-6 rounded-2xl bg-white border border-[#DBEAFE] p-5 flex items-end justify-between gap-4">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#64748B]">{t("b2b.perSeat")}</p>
                <p className="font-heading font-black text-2xl text-[#0B1B3A]" data-testid="b2b-unit-price">{unit}€ <span className="text-sm text-[#94A3B8] line-through font-semibold">{course?.price}€</span></p>
              </div>
              <div className="text-right">
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#64748B]">{t("b2b.total")}</p>
                <p className="font-heading font-black text-3xl text-[#1D4ED8]" data-testid="b2b-total-price">{total}€</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section" data-testid="b2b-form-section">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="font-heading font-black text-3xl sm:text-4xl text-[#0B1B3A]">{t("b2b.formTitle")}</h2>
            <p className="text-base text-[#475569] mt-4">{t("b2b.formSubtitle")}</p>
            <ul className="mt-8 space-y-3">
              {[t("b2b.step1"), t("b2b.step2"), t("b2b.step3")].map((s, i) => (
                <li key={i} className="flex gap-3 text-sm font-semibold text-[#0B1B3A]"><Check className="w-4 h-4 text-[#1D4ED8] mt-0.5 shrink-0" /> {s}</li>
              ))}
            </ul>
          </div>
          {sent ? (
            <div className="card-dark p-8 text-center" data-testid="b2b-success">
              <Check className="w-10 h-10 text-[#1D4ED8] mx-auto" />
              <h3 className="font-heading font-black text-xl mt-4 text-[#0B1B3A]">{t("b2b.sent")}</h3>
              <p className="text-sm text-[#475569] mt-2">{t("b2b.sentBody")}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="card-dark p-7 md:p-8 space-y-4" data-testid="b2b-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="b2b-name" required placeholder={t("lead.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-cream" />
                <input data-testid="b2b-organization" required placeholder={t("b2b.organization")} value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} className="input-cream" />
                <input data-testid="b2b-email" required type="email" placeholder={t("lead.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-cream" />
                <input data-testid="b2b-phone" placeholder={t("lead.phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-cream" />
              </div>
              <textarea data-testid="b2b-message" rows={4} placeholder={t("b2b.messagePh")} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="input-cream w-full" />
              <p className="text-xs text-[#64748B]">{t("b2b.quote")}: <b>{seats} × {unit}€ = {total}€</b> (−{pct}%) · {pick(course, "title", lang)}</p>
              <button data-testid="b2b-submit" disabled={sending} className="btn-blue w-full justify-center disabled:opacity-60">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} {t("b2b.submit")}
              </button>
            </form>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
